<template>
    <div class="main-container">
        <!-- Grab-themed Header -->
        <nav class="d-print-none" :class="['admin-header']">
            <div class="header-content">
                <div class="header-left">
                    <button @click="toggleSidebar" class="sidebar-toggle" type="button">
                        <i class="bi bi-list"></i>
                    </button>
                    <router-link to="/" class="brand-logo">
                        <img src="/favicon.ico" alt="Logo" class="logo-img" />
                        <span class="brand-text">Exodous CMS</span>
                    </router-link>
                </div>

                <div class="header-right">
                    <NotificationView />
                    <ProfileDropdown @logout="logout" @profile-navigate="navigateToProfile" />
                </div>
            </div>
        </nav>

        <div class="layout-container">
            <!-- Grab-themed Sidebar -->
            <aside class="w-print-0" :class="['sidebar', { 'sidebar-collapsed': isSidebarToggled }]">
                <nav class="sidebar-nav">
                    <!-- Dashboard -->
                    <router-link to="/" @click="handleNavClick" class="nav-item"
                        :class="{ active: $route.path === '/' }">
                        <div class="nav-icon">
                            <i class="bi bi-speedometer2"></i>
                        </div>
                        <span class="nav-text">Dashboard</span>
                        <div class="active-indicator"></div>
                    </router-link>

                    <!-- Rides Section -->
                    <div class="nav-section">
                        <div class="nav-header" @click="toggleSection('clients')">
                            <div class="nav-icon">
                                <i class="bi bi-person-lines-fill"></i>
                            </div>
                            <span class="nav-text">Clients</span>
                            <i class="bi bi-chevron-down section-arrow" :class="{ rotated: openSections.clients }"></i>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.clients }">
                            <router-link to="/clients/all"
                                v-if="currentUser && hasRequiredRole(['super admin', 'admin', 'treasurer'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/clients/all') }">
                                <i class="bi bi-clock"></i>
                                <span>All</span>
                                <div class="active-dot" v-if="$route.path.includes('/clients/all')"></div>
                            </router-link>
                            <router-link to="/clients/mt-active"
                                v-if="currentUser && hasRequiredRole(['super admin', 'admin', 'treasurer'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/clients/mt-active') }">
                                <i class="bi bi-check-circle"></i>
                                <span>Active On Mikrotik</span>
                                <div class="active-dot" v-if="$route.path.includes('/clients/mt-active')"></div>
                            </router-link>
                            <router-link to="/clients/heatmap"
                                v-if="currentUser && hasRequiredRole(['super admin', 'admin', 'treasurer'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/clients/heatmap') }">
                                <i class="bi bi-radar"></i>
                                <span>Heat Map</span>
                                <div class="active-dot" v-if="$route.path.includes('/clients/heatmap')"></div>
                            </router-link>
                        </div>
                    </div>

                    <!-- Users Section -->
                    <div class="nav-section">
                        <div class="nav-header" @click="toggleSection('users')">
                            <div class="nav-icon">
                                <i class="bi bi-people-fill"></i>
                            </div>
                            <span class="nav-text">Users</span>
                            <i class="bi bi-chevron-down section-arrow" :class="{ rotated: openSections.users }"></i>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.users }">
                            <router-link to="/users/list"
                                v-if="currentUser && hasRequiredRole(['admin', 'customersupport'])" class="nav-subitem"
                                :class="{ active: $route.path.includes('/users/list') }">
                                <i class="bi bi-person-check"></i>
                                <span>Active Users</span>
                                <div class="active-dot" v-if="$route.path.includes('/users/list')"></div>
                            </router-link>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.users }">
                            <router-link to="/users/cashflow"
                                v-if="currentUser && hasRequiredRole(['admin', 'useraccessmgmt'])" class="nav-subitem"
                                :class="{ active: $route.path.includes('/users/cashflow') }">
                                <i class="bi bi-person-check"></i>
                                <span>Cashflows</span>
                                <div class="active-dot" v-if="$route.path.includes('/users/cashflow')"></div>
                            </router-link>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.users }">
                            <router-link to="/users/roles"
                                v-if="currentUser && hasRequiredRole(['super admin', 'useraccessmgmt'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/users/roles') }">
                                <i class="bi bi-person-check"></i>
                                <span>User Roles</span>
                                <div class="active-dot" v-if="$route.path.includes('/users/roles')"></div>
                            </router-link>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.users }">
                            <router-link to="/users/feedbacks"
                                v-if="currentUser && hasRequiredRole(['super admin', 'bplo', 'useraccessmgmt'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/users/feedbacks') }">
                                <i class="bi bi-person-check"></i>
                                <span>User Feedbacks</span>
                                <div class="active-dot" v-if="$route.path.includes('/users/feedbacks')"></div>
                            </router-link>
                        </div>
                    </div>
                    <!-- Transactions Section -->
                    <div class="nav-section">
                        <div class="nav-header" @click="toggleSection('transactions')">
                            <div class="nav-icon">
                                <i class="bi bi-clock-history"></i>
                            </div>
                            <span class="nav-text">Transactions</span>
                            <i class="bi bi-chevron-down section-arrow"
                                :class="{ rotated: openSections.transactions }"></i>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.transactions }">
                            <router-link to="/transactions/receipts"
                                v-if="currentUser && hasRequiredRole(['admin', 'customersupport', 'tech', 'teller'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/transactions/receipts') }">
                                <i class="bi bi-receipt-cutoff"></i>
                                <span>Receipts</span>
                                <div class="active-dot" v-if="$route.path.includes('/transactions/receipts')"></div>
                            </router-link>
                        </div>
                        <div class="nav-submenu" :class="{ open: openSections.transactions }">
                            <router-link to="/transactions/remittances"
                                v-if="currentUser && hasRequiredRole(['admin', 'customersupport', 'tech', 'teller'])"
                                class="nav-subitem" :class="{ active: $route.path.includes('/transactions/remittances') }">
                                <i class="bi bi-credit-card-fill"></i>
                                <span>Remittances</span>
                                <div class="active-dot" v-if="$route.path.includes('/transactions/remittances')"></div>
                            </router-link>
                        </div>
                        <router-link to="/users/feedbacks"
                            v-if="currentUser && hasRequiredRole(['super admin', 'bplo', 'useraccessmgmt'])"
                            class="nav-subitem" :class="{ active: $route.path.includes('/users/feedbacks') }">
                            <i class="bi bi-person-check"></i>
                            <span>User Feedbacks</span>
                            <div class="active-dot" v-if="$route.path.includes('/users/feedbacks')"></div>
                        </router-link>
                    </div>


                    <!-- System Logs -->
                    <router-link v-if="currentUser && hasRequiredRole(['super admin', 'admin'])"
                        to="/system/user-action-logs" class="nav-item"
                        :class="{ active: $route.path.includes('/system/user-action-logs') }">
                        <div class="nav-icon">
                            <i class="bi bi-terminal"></i>
                        </div>
                        <span class="nav-text">System Logs</span>
                        <div class="active-indicator"></div>
                    </router-link>
                    <router-link v-if="currentUser && hasRequiredRole(['super admin', 'admin'])"
                        to="/system/payment-attempts" class="nav-item"
                        :class="{ active: $route.path.includes('/system/payment-attempts') }">
                        <div class="nav-icon">
                            <i class="bi bi-terminal"></i>
                        </div>
                        <span class="nav-text">Payment Attempts</span>
                        <div class="active-indicator"></div>
                    </router-link>
                </nav>
            </aside>

            <!-- Main Content -->
            <main :class="['main-content', { 'sidebar-collapsed': isSidebarToggled }]">
                <div class="content-wrapper">
                    <slot />
                </div>
            </main>
        </div>
    </div>
    <ConfirmDialog />
    <SpinnerOverlay />
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { hasRoles } from "@/utils/auth";
import confirmationService from "@/services/confirmationService";
import { roles } from "@/utils/config";
import router from "@/router";
import store from "@/store";
import ProfileDropdown from "../menus/ProfileDropdown.vue";
import NotificationView from "../notifications/NotificationView.vue";
import ConfirmDialog from "../animations/ConfirmDialog.vue";
import SpinnerOverlay from "../animations/SpinnerOverlay.vue";

// Define components
//eslint-disable-next-line
defineOptions({
    components: {
        NotificationView,
        ProfileDropdown,
        ConfirmDialog,
        SpinnerOverlay
    },
});

const route = useRoute();

// Reactive state
const isSidebarToggled = ref(false);
const showAdminHeader = ref(true);
const showLiveScan = ref(false);
const scanMode = ref("live");
const unreadNotifications = ref(0);

// Track open sections
const openSections = ref({
    clients: false,
    transactions: false,
    customers: false,
    payments: false,
    analytics: false,
    settings: false,
    bplsSetups: false,
    bplsReports: false,
    transparency: false,
});

// Computed properties
const currentUser = computed(() => store.getters["auth/currentUser"] ?? null);
const adminPrintRoles = computed(() => roles.adminPrint);

// Methods
const navigateToProfile = () => {
    router.push("/users/profile");
};

// Check if device is mobile
const isMobile = () => window.innerWidth <= 768;

// Auto-open sections based on current route
watch(route, (newRoute) => {
    // First, close ALL sections
    openSections.value = {
        clients: false,
        transacitons: false,
        users: false,
        payments: false,
        analytics: false,
        settings: false,
        transparency: false,
    };

    // Then, open only the relevant section based on current route
    if (newRoute.path.includes("/clients")) {
        openSections.value.clients = true;
    } else if (newRoute.path.includes("/transactions")) {
        openSections.value.transactions = true;
    } else if (newRoute.path.includes("/users")) {
        openSections.value.users = true;
    } else if (newRoute.path.includes("/payments")) {
        openSections.value.payments = true;
    } else if (newRoute.path.includes("/analytics")) {
        openSections.value.analytics = true;
    } else if (newRoute.path.includes("/settings")) {
        openSections.value.settings = true;
    }
    else if (newRoute.path.includes("/transparency")) {
        openSections.value.transparency = true;
    }

    // Auto-close sidebar on mobile when route changes
    if (isMobile()) {
        isSidebarToggled.value = true;
    }
});

const handleNavClick = () => {
    if (isMobile()) {
        isSidebarToggled.value = true; // Collapse sidebar on mobile after navigation
    }
};

// Close sidebar when clicking outside on mobile
const handleClickOutside = (event) => {
    if (
        isMobile() &&
        !event.target.closest(".sidebar") &&
        !event.target.closest(".sidebar-toggle") &&
        !isSidebarToggled.value
    ) {
        isSidebarToggled.value = true;
    }
};

// Handle window resize
const handleResize = () => {
    if (isMobile()) {
        // Collapse sidebar on mobile
        isSidebarToggled.value = true;
    } else {
        // Expand sidebar on desktop
        isSidebarToggled.value = false;
    }
};

const toggleSection = (section) => {
    if (section === 'bplsReports')
        openSections.value['bplsSetups'] = false;
    if (section === 'bplsSetups')
        openSections.value['bplsReports'] = false;
    openSections.value[section] = !openSections.value[section];
};

const onScannerClosed = () => {
    showLiveScan.value = false;
};

const toggleLiveScan = (mode) => {
    scanMode.value = mode;
    showLiveScan.value = !showLiveScan.value;
};

const hasRequiredRole = (requiredRoles) => {
    return hasRoles(requiredRoles);
};

const toggleSidebar = () => {
    isSidebarToggled.value = !isSidebarToggled.value;
};

const logout = async () => {
    const confirmed = await confirmationService.show("Are you sure you want to logout?");
    if (!confirmed) return;
    store.dispatch("auth/showAdminHeader", false);
    await store.dispatch("auth/logout");
};

// Watch route path for header visibility
watch(
    () => route.path,
    async (newPath) => {
        // Get header visibility from route meta
        const matchedRoute = route.matched.find((r) => r.path === newPath);
        const showHeader = matchedRoute?.meta?.showHeader !== false;

        // Update store
        store.dispatch("auth/setShowHeader", showHeader);
    },
    { immediate: true }
);

// Lifecycle hooks
onMounted(() => {
    showAdminHeader.value = store.getters["auth/showHeader"] ?? true;

    // Initialize sidebar state based on screen size
    if (isMobile()) {
        isSidebarToggled.value = true; // Collapsed by default on mobile
    } else {
        isSidebarToggled.value = false; // Expanded by default on desktop
    }

    // Initialize open sections based on current route
    if (route.path.includes("/clients")) openSections.value.clients = true;
    if (route.path.includes("/bpls/setups/")) openSections.value.bplsSetups = true;
    if (route.path.includes("/bpls/reports/")) openSections.value.bplsReports = true;
    if (route.path.includes("/transactions")) openSections.value.transactions = true;
    if (route.path.includes("/users")) openSections.value.users = true;
    if (route.path.includes("/payments")) openSections.value.payments = true;
    if (route.path.includes("/analytics")) openSections.value.analytics = true;
    if (route.path.includes("/settings")) openSections.value.settings = true;
    if (route.path.includes("/transparency")) openSections.value.transparency = true;
    if (route.path.includes("/login")) isSidebarToggled.value = true;

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
    window.removeEventListener("resize", handleResize);
});

onUnmounted(() => {
    //unsubscribe();
});

// Expose to template
//eslint-disable-next-line
defineExpose({
    isSidebarToggled,
    toggleSidebar,
    showAdminHeader,
    currentUser,
    logout,
    hasRequiredRole,
    adminPrintRoles,
    showLiveScan,
    toggleLiveScan,
    onScannerClosed,
    scanMode,
    unreadNotifications,
    openSections,
    toggleSection,
    handleNavClick,
    navigateToProfile,
});
</script>

<style scoped>
/* Main Layout */
.main-container {
    min-height: 80vh;
    max-height: 80vh;
    background: #f8f9fa;
}

/* Grab-themed Header */
.admin-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 64px;
    background: linear-gradient(135deg, #8A9A8A 0%, #6B7F6B 100%);
    border-bottom: 1px solid #008c3a;
    box-shadow: 0 2px 20px rgba(0, 177, 79, 0.2);
    z-index: 1000;
    backdrop-filter: blur(10px);
}

.header-hidden {
    height: 0;
    opacity: 0;
    overflow: hidden;
    border-bottom: none;
    box-shadow: none;
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* This is causing the centering */
    height: 100%;
    padding: 0 1.5rem;
    max-width: 100%;
}

/* Add these to ensure proper layout */
.header-left,
.header-right {
    flex: 0 0 auto;
    /* Prevent flex growing/shrinking */
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: max-content;
    /* Prevent collapsing */
}

.header-right {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: max-content;
    /* Prevent collapsing */
}

.sidebar-toggle {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    transition: all 0.3s ease;
    cursor: pointer;
}

.sidebar-toggle:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.brand-logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-decoration: none;
    color: white;
    font-weight: 700;
    font-size: 1.1rem;
}

.logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: white;
    padding: 4px;
}

.brand-text {
    color: white;
    font-weight: 800;
    letter-spacing: 0.5px;
}

/* Grab-themed Sidebar */
.layout-container {
    display: flex;
    margin-top: 64px;
    min-height: calc(100vh - 64px);
    max-height: calc(100vh - 64px);
}

.sidebar {
    width: 280px;
    background: linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%);
    border-right: 1px solid #333;
    transition: all 0.3s ease;
    position: fixed;
    top: 64px;
    left: 0;
    bottom: 0;
    z-index: 999;
    overflow-y: auto;

    /* Modern WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 3px;
        transition: background 0.3s ease;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

    /* Smooth scrolling */
    scroll-behavior: smooth;
}

@media print {
    .w-print-0 {
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
    }

    .sidebar {
        width: 0 !important;
    }

    .main-content {
        margin-left: 0 !important;
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
    }

    /* Also handle the collapsed state for print */
    .main-content.sidebar-collapsed {
        margin-left: 0 !important;
        width: 100% !important;
    }

    .admin-header {
        height: 0 !important;
    }

    .layout-container {
        display: flex;
        margin-top: 0;
    }

}

/* Optional: If you want to completely hide scrollbar when not in use */
.sidebar.hide-scrollbar::-webkit-scrollbar {
    width: 0;
}

.sidebar.hide-scrollbar {
    scrollbar-width: none;
}

/* For Internet Explorer/Edge Legacy */
@media screen and (-ms-high-contrast: active),
(-ms-high-contrast: none) {
    .sidebar {
        -ms-overflow-style: -ms-autohiding-scrollbar;
    }
}

/* Default state: collapsed on mobile, expanded on desktop */
.sidebar-collapsed {
    width: 0;
}

/* Desktop: sidebar visible by default */
@media (min-width: 769px) {
    .sidebar {
        transform: translateX(0);
    }

    .sidebar-collapsed {
        width: 0;
    }
}

/* Mobile: sidebar hidden by default */
@media (max-width: 768px) {
    .sidebar {
        width: 280px;
    }

    .sidebar:not(.sidebar-collapsed) {
        transform: translateX(0);
    }

    .sidebar.sidebar-collapsed {
        transform: translateX(-100%);
    }
}

.sidebar-nav {
    padding: 1.5rem 0;
}

/* Navigation Items */
.nav-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    color: #e0e0e0;
    text-decoration: none;
    transition: all 0.3s ease;
    position: relative;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
}

.nav-item:hover {
    background: rgba(0, 177, 79, 0.1);
    color: #00b14f;
}

.nav-item.active {
    background: linear-gradient(90deg,
            rgba(0, 177, 79, 0.15) 0%,
            rgba(0, 177, 79, 0.05) 100%);
    color: #00b14f;
    border-right: 3px solid #00b14f;
}

.nav-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nav-icon i {
    font-size: 1.1rem;
}

.nav-text {
    font-weight: 500;
    font-size: 0.95rem;
}

.active-indicator {
    position: absolute;
    right: 1rem;
    width: 6px;
    height: 6px;
    background: #00b14f;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.nav-item.active .active-indicator {
    opacity: 1;
}

/* Navigation Sections */
.nav-section {
    margin-bottom: 0.5rem;
}

.nav-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    color: #e0e0e0;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
}

.nav-header:hover {
    background: rgba(0, 177, 79, 0.1);
    color: #00b14f;
}

.section-arrow {
    margin-left: auto;
    font-size: 0.8rem;
    transition: transform 0.3s ease;
    color: #888;
}

.section-arrow.rotated {
    transform: rotate(180deg);
    color: #00b14f;
}

/* Submenu */
.nav-submenu {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
}

.nav-submenu.open {
    max-height: 1000px;
}

.nav-subitem {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem 0.75rem 3.5rem;
    color: #b0b0b0;
    text-decoration: none;
    transition: all 0.3s ease;
    position: relative;
    font-size: 0.9rem;
}

.nav-subitem:hover {
    background: rgba(0, 177, 79, 0.08);
    color: #00b14f;
    padding-left: 3.75rem;
}

.nav-subitem.active {
    color: #00b14f;
    background: rgba(0, 177, 79, 0.12);
}

.nav-subitem i {
    width: 16px;
    font-size: 0.9rem;
}

.active-dot {
    width: 6px;
    height: 6px;
    background: #00b14f;
    border-radius: 50%;
    margin-left: auto;
}

/* Main Content - FIXED LAYOUT */
.main-content {
    flex: 1;
    margin-left: 280px;
    transition: margin-left 0.3s ease;
    min-height: calc(100vh - 64px);
    max-height: calc(100vh - 64px);
    width: calc(100% - 280px);
}

.main-content.sidebar-collapsed {
    margin-left: 0;
    width: 100%;
}

.content-wrapper {
    min-height: calc(100vh - 180px);
    background: #f8f9fa;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .sidebar {
        width: 250px;
    }

    .main-content {
        margin-left: 250px;
        width: calc(100% - 250px);
    }
}

@media (max-width: 768px) {
    .main-content {
        margin-left: 0 !important;
        width: 100% !important;
    }

    .header-content {
        padding: 0 1rem;
    }

    .content-wrapper {
        padding: 1.5rem 1rem;
    }

    .brand-text {
        display: none;
    }
}

@media (max-width: 480px) {
    .content-wrapper {
        padding: 1rem 0.75rem;
    }
}

/* Custom Scrollbar */
.sidebar::-webkit-scrollbar {
    width: 4px;
}

.sidebar::-webkit-scrollbar-track {
    background: #333;
}

.sidebar::-webkit-scrollbar-thumb {
    background: #00b14f;
    border-radius: 2px;
}

.sidebar::-webkit-scrollbar-thumb:hover {
    background: #00a046;
}
</style>
